/**
 * @module utils
 * @package @geenius/adapters/react
 * @description Provides React-specific utility helpers for composing Tailwind
 * class names. The module wraps `clsx` and `tailwind-merge` to keep React
 * component styling deterministic and ergonomic.
 */

import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merge CSS class names with Tailwind support.
 * Combines clsx (for conditional classes) with tailwind-merge (to resolve conflicts).
 *
 * This utility ensures that Tailwind classes override each other correctly:
 * cn('px-2', 'px-4') → 'px-4' (not both)
 * Also handles conditional classes:
 * cn('p-2', isActive && 'bg-primary')
 *
 * @param inputs - Class names, arrays, objects, or conditional values
 * @returns Merged and deduplicated class string
 *
 * @example
 * ```ts
 * cn('px-2 py-1', 'px-4') // 'py-1 px-4'
 * cn('p-2', isActive && 'bg-primary') // 'p-2 bg-primary' or 'p-2'
 * cn({ 'text-destructive': isError, 'text-primary': isSuccess }) // conditional classes
 * ```
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
